import { useEffect, useRef } from 'react';

import { useLoyaltyOfferGroupsExperiment } from 'experiments/use-loyalty-offer-groups-experiment';
import { useFeatureSortedLoyaltyOfferGroupsLazyQuery } from 'generated/sanity-graphql';
import { withLazySimpleCache } from 'state/graphql/with-simple-cache';
import { useLocale } from 'state/intl';
import { LANGUAGES } from 'state/intl/types';

import { LoyaltyOfferGroup } from '../types';

import { compareBy_id, useConditionalState } from './use-conditional-state';

const useCachedFeatureLoyaltyOfferGroupsLazyQuery = withLazySimpleCache(
  useFeatureSortedLoyaltyOfferGroupsLazyQuery
);

export const useLoyaltyOfferGroups = () => {
  const { language } = useLocale();
  const prevLanguageRef = useRef<LANGUAGES>(language);
  const offerGroupExperimentEnabled = useLoyaltyOfferGroupsExperiment(false);
  const [
    queryOfferGroups,
    { data, loading = false, refetch },
  ] = useCachedFeatureLoyaltyOfferGroupsLazyQuery({
    variables: {
      id: offerGroupExperimentEnabled
        ? 'feature-loyalty-offers-ui-singleton-experiment'
        : 'feature-loyalty-offers-ui-singleton',
    },
  });

  const [offerGroups, setOfferGroups] = useConditionalState(
    [] as LoyaltyOfferGroup[],
    compareBy_id
  );

  useEffect(() => {
    const languageChanged = prevLanguageRef.current !== language;
    if (languageChanged) {
      refetch();
    }
    prevLanguageRef.current = language;
  }, [refetch, language]);

  useEffect(() => {
    if (offerGroupExperimentEnabled) {
      queryOfferGroups({ allowFromCache: false });
    }
  }, [offerGroupExperimentEnabled, queryOfferGroups]);

  useEffect(() => {
    if (data?.LoyaltyOffersUI?.sortedSystemwideOfferGroups) {
      setOfferGroups(data.LoyaltyOffersUI?.sortedSystemwideOfferGroups as LoyaltyOfferGroup[]);
    }
  }, [data, setOfferGroups]); // setOfferGroups is stable but ESLint doesn't know

  return {
    loading,
    refetch,
    offerGroups,
  };
};
